/**
 * Scen / Tross catalog sections on /produkter?category=Scen / Tross.
 * Classification is use-case via slug/id — product display names must not move tiers.
 */

export type ScenTrossTierId =
  | 'scenpodier'
  | 'tross_konstruktion'
  | 'tillbehor_komponenter';

export const SCEN_TROSS_TIER_STICKY_NAV = false;

export const SCEN_TROSS_TIER_ORDER: readonly ScenTrossTierId[] = [
  'scenpodier',
  'tross_konstruktion',
  'tillbehor_komponenter',
] as const;

export const SCEN_TROSS_TIER_COPY: Readonly<
  Record<ScenTrossTierId, { title: string; blurb: string }>
> = {
  scenpodier: {
    title: 'Scenpodier & scener',
    blurb:
      'Scenlösningar för allt från tal och presentationer till bröllop, företagsevent och större produktioner.',
  },
  tross_konstruktion: {
    title: 'Tross & konstruktion',
    blurb:
      'Trossystem för ljus, dekor, teknik och eventproduktion. Från enskilda sektioner till kompletta trosskonstruktioner.',
  },
  tillbehor_komponenter: {
    title: 'Tillbehör & komponenter',
    blurb:
      'Scenben, trappor, basplattor och andra komponenter för att komplettera din scen- eller trosslösning.',
  },
};

/** Explicit live mapping (verified 2026-09-18). One section per product. */
export const SCEN_TROSS_TIER_BY_SLUG: Readonly<Record<string, ScenTrossTierId>> = {
  // Scenpodier & scener
  'scenpodie-1x0-5-m': 'scenpodier',
  'scenpodie-2x0-5m': 'scenpodier',
  'scenpodie-2x1-m': 'scenpodier',
  'scener-scenpodier-tross-kravallstaket-offertbaserade-losningar': 'scenpodier',
  // Tross & konstruktion
  'tross-0-25-m': 'tross_konstruktion',
  'tross-0-5-m': 'tross_konstruktion',
  'tross-0-75-m': 'tross_konstruktion',
  'tross-1-meter': 'tross_konstruktion',
  'tross-2-meter': 'tross_konstruktion',
  'tross-3-meter': 'tross_konstruktion',
  'tross-90-grader': 'tross_konstruktion',
  'tross-t-horn': 'tross_konstruktion',
  'trevags-tross': 'tross_konstruktion',
  // Tillbehör & komponenter
  'scenben-30cm': 'tillbehor_komponenter',
  'scenben-50cm': 'tillbehor_komponenter',
  'scenben-70cm': 'tillbehor_komponenter',
  'scenben-100cm': 'tillbehor_komponenter',
  'scentrappa': 'tillbehor_komponenter',
  'basplatta-till-tross': 'tillbehor_komponenter',
};

/** Stable product ids for the same live set (secondary SoT if slug missing). */
export const SCEN_TROSS_TIER_BY_ID: Readonly<Record<string, ScenTrossTierId>> = {
  '3acb5f95-99e2-432a-b679-0685e6fbef27': 'scenpodier',
  'b3f788c0-091d-4191-af80-a73eeb858572': 'scenpodier',
  '1b3b9012-f5aa-4cbd-a1a1-41f9ddb88edd': 'scenpodier',
  '6449231e-acc8-4729-82c9-5b7184ddd970': 'scenpodier',
  '6afbb053-3fc7-4751-a53a-5c912e12dfc5': 'tross_konstruktion',
  '9d49feac-1486-416e-8d6e-b24d95ad896b': 'tross_konstruktion',
  '7af10bc2-ad6f-4bd0-97f8-427be98c6d0c': 'tross_konstruktion',
  'c38c6c07-f84e-4780-a7c5-a67d656d3de5': 'tross_konstruktion',
  'f0d0cba2-97b9-46eb-ad00-19ac828575f9': 'tross_konstruktion',
  '4a18ab8b-28fd-4e87-a1c4-d33366812d5e': 'tross_konstruktion',
  '0651bdfe-d56c-4cd4-94d2-cb8b47976c84': 'tross_konstruktion',
  'c9e963f8-e88a-46cc-b003-d0fe531a0b03': 'tross_konstruktion',
  '96b3b3bf-2dff-4913-93d1-88c48ab04445': 'tross_konstruktion',
  'e56625a2-6287-406e-98bc-58a8f5120e18': 'tillbehor_komponenter',
  '856b9a83-eb1e-441e-a5ba-fdb0265717a5': 'tillbehor_komponenter',
  '2a98fe96-c87e-4ddf-adc9-de1d485facf7': 'tillbehor_komponenter',
  'e60e3321-9fd0-4a48-ba01-e8a8aa25b9b0': 'tillbehor_komponenter',
  '9597c89d-c438-4ae7-81fc-570ae270232e': 'tillbehor_komponenter',
  'fbf31224-fe7d-4f6c-8f93-b9de4da602c4': 'tillbehor_komponenter',
};

export type ScenTrossTierProductRef = {
  id?: string;
  slug?: string | null;
  name?: string;
};

/**
 * Resolve section. Explicit slug, then id. Name is ignored.
 * Returns null for unmapped products (no implicit fallback section).
 */
export function resolveScenTrossTier(
  product: ScenTrossTierProductRef
): ScenTrossTierId | null {
  const slug = String(product.slug ?? '')
    .trim()
    .toLowerCase();
  if (slug && SCEN_TROSS_TIER_BY_SLUG[slug]) {
    return SCEN_TROSS_TIER_BY_SLUG[slug];
  }
  const id = String(product.id ?? '').trim();
  if (id && SCEN_TROSS_TIER_BY_ID[id]) {
    return SCEN_TROSS_TIER_BY_ID[id];
  }
  return null;
}

export type ScenTrossTierGroup<T extends ScenTrossTierProductRef> = {
  tier: ScenTrossTierId;
  title: string;
  blurb: string;
  products: T[];
};

export function groupScenTrossProducts<T extends ScenTrossTierProductRef>(
  products: readonly T[]
): ScenTrossTierGroup<T>[] {
  const buckets: Record<ScenTrossTierId, T[]> = {
    scenpodier: [],
    tross_konstruktion: [],
    tillbehor_komponenter: [],
  };
  for (const product of products) {
    const tier = resolveScenTrossTier(product);
    if (!tier) continue;
    buckets[tier].push(product);
  }
  return SCEN_TROSS_TIER_ORDER.map((tier) => ({
    tier,
    title: SCEN_TROSS_TIER_COPY[tier].title,
    blurb: SCEN_TROSS_TIER_COPY[tier].blurb,
    products: buckets[tier],
  })).filter((g) => g.products.length > 0);
}
